import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { ActionIcon } from '@mantine/core';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { pdf } from '@react-pdf/renderer';
import { useCallback, useState } from 'react';
import { secondaryBase } from '../../../../styles/design-tokens';
import type { ArtifactSpec, Message } from '../../../flora/flora.type';
import { captureArtifact } from './agent-chat-export.helpers';
import { buildPdfDocument, ensureEmojiFont } from './agent-chat-export.renderers';

interface AgentChatExportButtonProps {
  questions: Message[];
  messages: Message[];
  chatName?: string;
  disabled?: boolean;
  variant?: 'default' | 'transparent';
  artifactPanelOpen?: boolean;
  onOpenArtifactPanel?: () => void;
  onExportingChange?: (exporting: boolean) => void;
}

export function AgentChatExportButton({
  questions,
  messages,
  chatName,
  disabled,
  variant = 'default',
  artifactPanelOpen,
  onOpenArtifactPanel,
  onExportingChange,
}: AgentChatExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting || disabled) return;
    setIsExporting(true);
    onExportingChange?.(true);

    // Collect all artifacts from all messages
    const allArtifacts: ArtifactSpec[] = messages.flatMap((m) => m.artifacts ?? []);

    // Open the artifacts panel if closed so DOM elements are visible for capture
    if (allArtifacts.length > 0 && !artifactPanelOpen && onOpenArtifactPanel) {
      onOpenArtifactPanel();
      // Wait for React to render the panel and mount artifact cards
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    // Expand any collapsed artifact cards so their content is in the DOM for capture.
    if (allArtifacts.length > 0) {
      for (const artifact of allArtifacts) {
        const card = document.getElementById(`artifact-${artifact.id}`);
        if (!card) continue;
        const chevron = card.querySelector('[data-collapsed="true"]');
        if (chevron) {
          const header = card.querySelector<HTMLElement>('[role="button"]');
          header?.click();
        }
      }

      // Wait for expanded content to render and charts to disable animations
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }

    // Capture each artifact as a PNG blob URL (temporary DOM mount)
    const artifactImages = new Map<string, string>();

    try {
      ensureEmojiFont();

      for (const artifact of allArtifacts) {
        const imgData = await captureArtifact(artifact);
        if (imgData) {
          artifactImages.set(artifact.id, imgData);
        }
      }

      // Build the react-pdf document
      const doc = buildPdfDocument(questions, messages, chatName, artifactImages);

      // Generate blob and trigger download
      const blob = await pdf(doc as Parameters<typeof pdf>[0]).toBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${(chatName ?? 'flora-chat').replace(/[^a-z0-9]/gi, '_').toLowerCase()}-export.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Export] PDF export failed:', err);
    } finally {
      // Revoke all artifact blob URLs to prevent memory leaks
      for (const blobUrl of artifactImages.values()) {
        URL.revokeObjectURL(blobUrl);
      }
      setIsExporting(false);
      onExportingChange?.(false);
    }
  }, [isExporting, disabled, questions, messages, chatName, artifactPanelOpen, onOpenArtifactPanel, onExportingChange]);

  const tooltipLabel = disabled ? 'Streaming in progress…' : isExporting ? 'Preparing PDF…' : 'Export chat as PDF';
  const iconName = isExporting ? 'material-symbols:hourglass-empty' : 'material-symbols:download';
  const isDisabled = disabled || isExporting;

  if (variant === 'transparent') {
    return (
      <Tooltip label={tooltipLabel} variant="black">
        <ActionIcon variant="transparent" onClick={handleExport} disabled={isDisabled} aria-label="Export chat as PDF">
          <Icon
            icon={iconName}
            width={22}
            height={22}
            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', color: secondaryBase }}
          />
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltipLabel} variant="black" position="bottom" withArrow withinPortal={false}>
      <ExportBtn
        onClick={handleExport}
        disabled={isDisabled}
        type="button"
        aria-label="Export chat as PDF"
        data-exporting={isExporting}
      >
        <Icon icon={iconName} width={16} height={16} />
      </ExportBtn>
    </Tooltip>
  );
}

const ExportBtn = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--sky-base);
    color: var(--ink-darkest);
    background: var(--sky-lighter);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &[data-exporting='true'] {
    border-color: var(--secondary-light);
    color: var(--secondary-base);
    background: var(--secondary-lightest);
  }
`;
